import _ from "lodash";
import classNames from "classnames";

export type Select3Props<
  Keys extends keyof M,
  M extends { [key: string]: string },
> = Select3WithNullValue<Keys, M> | Select3WithDefaultKey<Keys, M>;

//case where you want something to be displayed when nothing has been choosen (value = null)
interface Select3WithNullValue<
  Keys extends keyof M,
  M extends { [key: string]: string },
> {
  keyMap: M;
  onSelect: (key: Keys | null) => void;
  nullDisplay: string;
  selected: Keys | null;
  className: string;
  disabled?: boolean;
  testid?: string;
  id?: string;
  width?: string;
  smallHeight?: boolean;
}

//case where you want a default value to be selected (you can't select nothing)
interface Select3WithDefaultKey<
  Keys extends keyof M,
  M extends { [key: string]: string },
> {
  keyMap: M;
  onSelect: (key: Keys) => void;
  defaultKey: string;
  selected: Keys;
  className: string;
  disabled?: boolean;
  testid?: string;
  id?: string;
  sort?: boolean;
  width?: string;
  smallHeight?: boolean;
}

const Select3 = <Keys extends keyof M, M extends { [key: string]: string }>(
  props: Select3Props<Keys, M>
) => {
  const style = props.width ? { width: props.width } : {};
  const handleChange = (value: string) => {
    if ("nullDisplay" in props && value === props.nullDisplay) {
      props.onSelect(null);
    } else {
      props.onSelect(value as Keys);
    }
  };

  const keyLabels =
    "sort" in props && props.sort
      ? _.sortBy(Object.entries(props.keyMap), ([_key, label]) => label)
      : Object.entries(props.keyMap);

  return (
    <select
      onChange={(event) => handleChange(event.target.value)}
      className={classNames(
        props.className,
        "border rounded-md p-2 border-slate-300 border-solid bg-white transition-colors focus:outline-none focus:border-blue-700 focus:transition-all focus:shadow-blue-700",
        {
          ["h-8"]: props.smallHeight,
          ["h-10"]: !props.smallHeight,
        }
      )}
      value={
        props.selected
          ? (props.selected as string)
          : "defaultKey" in props
            ? props.defaultKey
            : props.nullDisplay
      }
      disabled={props.disabled}
      data-testid={props.testid || ""}
      id={props.id || ""}
      style={style}
    >
      {"nullDisplay" in props ? (
        <option value={props.nullDisplay} key={"emptyKey"}>
          {props.nullDisplay}
        </option>
      ) : null}
      {keyLabels.map(([key, label]) => (
        <option value={key} key={key}>
          {label}
        </option>
      ))}
    </select>
  );
};

export default Select3;
